import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { setPostLoginRedirect } from '../../helpers/storage/storage';
import { SessionStatus, useSessionRestore } from '../../hooks/useSessionRestore';
import { BrandedLoadingOverlay } from '../loader/branded-loader';

interface ProtectedRouteProps {
  children?: ReactNode;
}

/**
 * Route guard that only renders its children for an authenticated session.
 *
 * Responsibilities:
 * 1. While a load-time refresh is resolving (`Restoring`), show the branded
 *    loader instead of redirecting — an expired access token with a valid
 *    refresh token is recoverable, not a logout
 * 2. When there is no recoverable session (`Unauthenticated`), capture the
 *    intended destination (path + query + hash) and redirect to login
 * 3. Otherwise render the protected children
 *
 * @param {ProtectedRouteProps} props - Component props
 * @returns {ReactNode} The children, the loader, or a redirect to login
 */
export function ProtectedRoute({ children }: ProtectedRouteProps): ReactNode {
  const status = useSessionRestore();
  const location = useLocation();

  if (status === SessionStatus.Restoring) {
    // role="status" gives assistive tech a live-region cue while the route is
    // withheld during a load-time restore.
    return (
      <div role="status" aria-label="Restoring your session">
        <BrandedLoadingOverlay visible variant="colored" />
      </div>
    );
  }

  if (status === SessionStatus.Unauthenticated) {
    // Capture the full intended destination before redirecting to login so the
    // user is returned to it after signing back in.
    setPostLoginRedirect(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to="/" />;
  }

  return children;
}
